// crewbit-admin/src/components/money/SendInvoiceModal.tsx
//
// Centered confirm modal for invoice send. v1: marks the invoice as sent
// via mark_invoice_sent (RPC handles activity_log + status transition).
// Email dispatch itself is handled by the existing useInvoiceSend hook +
// pending_notifications worker — this modal is the operator confirmation.

import { useState } from 'react';
import { Send } from 'lucide-react';
import { palette, spacing, typography, transition } from '../../styles/tokens';
import { markSent } from '../../lib/invoiceMutations';

interface Props {
  isOpen: boolean;
  invoiceId: string;
  clientEmail: string | null | undefined;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

export function SendInvoiceModal({ isOpen, invoiceId, clientEmail, onClose, onResult }: Props) {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const hasEmail = !!clientEmail && clientEmail.trim().length > 0;

  const onConfirm = async () => {
    if (!hasEmail) return;
    setLoading(true);
    const result = await markSent(invoiceId, 'email');
    setLoading(false);
    if (result.success) {
      onResult(true, result.data.already_sent ? 'Invoice already sent.' : 'Invoice marked as sent.');
      onClose();
    } else {
      onResult(false, result.error.message ?? 'Failed to send invoice.');
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 420, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.accentWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <Send size={22} color={palette.accent} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Send invoice
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          {hasEmail
            ? <>Will be emailed to <span style={{ color: palette.g800, fontWeight: 600 }}>{clientEmail}</span>.</>
            : 'Add a client email before sending this invoice.'}
        </div>

        {!hasEmail && (
          <div style={{
            background: palette.amberWash, color: palette.amber,
            border: `1px solid ${palette.amber}`,
            borderRadius: 8, padding: spacing.sm,
            fontSize: typography.size.caption, marginBottom: spacing.md,
          }}>
            No client email on file. Edit the invoice to add one.
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading || !hasEmail} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: (loading || !hasEmail) ? palette.g300 : palette.accent,
            color: palette.white, fontSize: 13.5, fontWeight: 600,
            cursor: (loading || !hasEmail) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? 'Sending…' : 'Send invoice'}</button>
        </div>
      </div>
    </>
  );
}
